import { Toast as BaseToast } from "@base-ui/react/toast";
import { useMemo, type ReactNode } from "react";
import { Icon, type IconName } from "../Icon/Icon";
import { IconButton } from "../IconButton/IconButton";
import styles from "./Toast.module.css";

export type ToastTone = "neutral" | "success" | "warning" | "danger" | "info";

const TONE_ICONS: Record<ToastTone, IconName | null> = {
  neutral: null,
  success: "checkCircle",
  warning: "alertTriangle",
  danger: "alertCircle",
  info: "info",
};

/** Auto-dismiss after 6 s (paused on hover and focus); failures persist until dismissed. */
const DEFAULT_TIMEOUT_MS = 6000;
/** Undo stays available a little longer (about 8 s). */
const UNDO_TIMEOUT_MS = 8000;

export interface ToastOptions {
  /** What happened, using the action's verb: "Lot T030-24S received". */
  title: ReactNode;
  description?: ReactNode;
  tone?: ToastTone;
  /** One action: Undo for reversible changes, Retry for failures. */
  action?: { label: string; onClick: () => void };
  /** Milliseconds; 0 keeps the toast until dismissed. */
  timeout?: number;
  id?: string;
}

export interface ToastApi {
  show: (options: ToastOptions) => string;
  success: (title: ReactNode, options?: Omit<ToastOptions, "title" | "tone">) => string;
  /** Persists until dismissed. Errors the user must fix in a form stay inline instead. */
  error: (title: ReactNode, options?: Omit<ToastOptions, "title" | "tone">) => string;
  info: (title: ReactNode, options?: Omit<ToastOptions, "title" | "tone">) => string;
  dismiss: (id?: string) => void;
}

function ToastList() {
  const { toasts } = BaseToast.useToastManager();
  return toasts.map((toast) => {
    const tone = (toast.type ?? "neutral") as ToastTone;
    const icon = TONE_ICONS[tone];
    return (
      <BaseToast.Root key={toast.id} toast={toast} className={styles.toast} data-tone={tone}>
        <BaseToast.Content className={styles.content}>
          {icon ? (
            <span className={styles.icon}>
              <Icon name={icon} />
            </span>
          ) : null}
          <div className={styles.text}>
            <BaseToast.Title className={styles.title} />
            <BaseToast.Description className={styles.description} />
          </div>
          <BaseToast.Action className={styles.action} />
          <BaseToast.Close render={<IconButton icon="x" label="Dismiss" tooltip={false} size="sm" />} />
        </BaseToast.Content>
      </BaseToast.Root>
    );
  });
}

/** Provides toasts to the app (at most three visible) and renders the viewport. Mount once. */
export function ToastProvider({ children }: { children: ReactNode }) {
  return (
    <BaseToast.Provider limit={3} timeout={DEFAULT_TIMEOUT_MS}>
      {children}
      <BaseToast.Portal>
        <BaseToast.Viewport className={styles.viewport}>
          <ToastList />
        </BaseToast.Viewport>
      </BaseToast.Portal>
    </BaseToast.Provider>
  );
}

/** Confirm what the user just did or report background work: toast.success("Lot T030-24S received"). */
export function useToast(): ToastApi {
  const manager = BaseToast.useToastManager();
  return useMemo(() => {
    function show({ title, description, tone = "neutral", action, timeout, id }: ToastOptions): string {
      const defaultTimeout = tone === "danger" ? 0 : action ? UNDO_TIMEOUT_MS : DEFAULT_TIMEOUT_MS;
      return manager.add({
        id,
        title,
        description,
        type: tone,
        timeout: timeout ?? defaultTimeout,
        priority: tone === "danger" ? "high" : "low",
        actionProps: action ? { children: action.label, onClick: action.onClick } : undefined,
      });
    }
    return {
      show,
      success: (title, options) => show({ ...options, title, tone: "success" }),
      error: (title, options) => show({ ...options, title, tone: "danger" }),
      info: (title, options) => show({ ...options, title, tone: "info" }),
      dismiss: (id) => manager.close(id),
    };
  }, [manager]);
}
